import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Download, Sparkles, Mail, Phone, User as UserIcon, Compass } from "lucide-react";
import { API } from "@/lib/constants";

const L = {
  en: { title: "Almost there!", sub: "Enter your details to unlock your personalised WA guide (PDF).", name: "First name (optional)", email: "Your email", phone_hint: "Country code + number", source: "How did you hear about us?", consent: "I agree to receive Australia travel tips by email. Unsubscribe anytime.", download: "Download my guide (PDF)", ok: "Guide ready — check your downloads!", err_invalid_phone: "Invalid phone. Use format like +33612345678", err_consent: "Please tick the consent box", err_generic: "Something went wrong. Try again.", src_placeholder: "Choose…", sources: { instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook group", friend: "A friend / word of mouth", google: "Google search", other: "Other" } },
  fr: { title: "Presque là !", sub: "Renseigne tes infos pour débloquer ton guide WA personnalisé (PDF).", name: "Prénom (optionnel)", email: "Ton email", phone_hint: "Indicatif pays + numéro", source: "Comment tu m'as connu ?", consent: "J'accepte de recevoir des tips voyage Australie par email. Désinscription à tout moment.", download: "Télécharger mon guide (PDF)", ok: "Guide prêt — check tes téléchargements !", err_invalid_phone: "Téléphone invalide. Format attendu: +33612345678", err_consent: "Merci de cocher la case", err_generic: "Une erreur est survenue. Réessaye.", src_placeholder: "Choisir…", sources: { instagram: "Instagram", tiktok: "TikTok", facebook: "Groupe Facebook", friend: "Un pote / bouche à oreille", google: "Recherche Google", other: "Autre" } },
  de: { title: "Fast geschafft!", sub: "Fülle deine Daten aus, um deinen personalisierten WA-Guide (PDF) zu erhalten.", name: "Vorname (optional)", email: "Deine E-Mail", phone_hint: "Ländercode + Nummer", source: "Wie hast du von uns erfahren?", consent: "Ich möchte Australien-Reisetipps per E-Mail erhalten. Jederzeit abbestellbar.", download: "Guide herunterladen (PDF)", ok: "Guide fertig — schau in deine Downloads!", err_invalid_phone: "Ungültige Nummer. Format: +33612345678", err_consent: "Bitte Einwilligung ankreuzen", err_generic: "Fehler. Versuche es erneut.", src_placeholder: "Auswählen…", sources: { instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook-Gruppe", friend: "Freund / Mundpropaganda", google: "Google-Suche", other: "Andere" } },
  it: { title: "Ci sei quasi!", sub: "Inserisci i tuoi dati per ottenere la tua guida WA personalizzata (PDF).", name: "Nome (opzionale)", email: "La tua email", phone_hint: "Prefisso + numero", source: "Come ci hai conosciuti?", consent: "Accetto di ricevere consigli di viaggio via email. Disiscrizione in qualsiasi momento.", download: "Scarica la mia guida (PDF)", ok: "Guida pronta — controlla i download!", err_invalid_phone: "Telefono non valido. Formato: +33612345678", err_consent: "Spunta la casella di consenso", err_generic: "Errore. Riprova.", src_placeholder: "Scegli…", sources: { instagram: "Instagram", tiktok: "TikTok", facebook: "Gruppo Facebook", friend: "Amico / passaparola", google: "Ricerca Google", other: "Altro" } },
  es: { title: "¡Casi listo!", sub: "Rellena tus datos para desbloquear tu guía WA personalizada (PDF).", name: "Nombre (opcional)", email: "Tu email", phone_hint: "Prefijo + número", source: "¿Cómo nos conociste?", consent: "Acepto recibir consejos de viaje por email. Baja en cualquier momento.", download: "Descargar mi guía (PDF)", ok: "Guía lista — ¡mira tus descargas!", err_invalid_phone: "Teléfono no válido. Formato: +33612345678", err_consent: "Marca la casilla de consentimiento", err_generic: "Error. Inténtalo de nuevo.", src_placeholder: "Elige…", sources: { instagram: "Instagram", tiktok: "TikTok", facebook: "Grupo de Facebook", friend: "Amigo / boca a boca", google: "Búsqueda Google", other: "Otro" } },
  pt: { title: "Quase lá!", sub: "Preenche os teus dados para desbloquear o teu guia WA personalizado (PDF).", name: "Nome (opcional)", email: "O teu email", phone_hint: "Indicativo + número", source: "Como nos conheceste?", consent: "Aceito receber dicas de viagem por email. Cancelar a qualquer momento.", download: "Descarregar o meu guia (PDF)", ok: "Guia pronto — vê os teus downloads!", err_invalid_phone: "Telefone inválido. Formato: +33612345678", err_consent: "Marca a caixa de consentimento", err_generic: "Erro. Tenta de novo.", src_placeholder: "Escolher…", sources: { instagram: "Instagram", tiktok: "TikTok", facebook: "Grupo Facebook", friend: "Amigo / boca a boca", google: "Pesquisa Google", other: "Outro" } },
};

const COUNTRY_CODES = [
  { code: "+61", flag: "🇦🇺", name: "AU" },
  { code: "+33", flag: "🇫🇷", name: "FR" },
  { code: "+49", flag: "🇩🇪", name: "DE" },
  { code: "+39", flag: "🇮🇹", name: "IT" },
  { code: "+34", flag: "🇪🇸", name: "ES" },
  { code: "+351", flag: "🇵🇹", name: "PT" },
  { code: "+44", flag: "🇬🇧", name: "UK" },
  { code: "+1", flag: "🇺🇸", name: "US/CA" },
  { code: "+81", flag: "🇯🇵", name: "JP" },
  { code: "+82", flag: "🇰🇷", name: "KR" },
  { code: "+55", flag: "🇧🇷", name: "BR" },
];

export default function GuideLeadModal({ open, onClose, lang = "en", prefill = {} }) {
  const t = L[lang] || L.en;
  const [name, setName] = useState(prefill.name || "");
  const [email, setEmail] = useState(prefill.email || "");
  const [cc, setCc] = useState("+61");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) { setError(t.err_consent); return; }
    const cleanPhone = (phone || "").replace(/[^\d]/g, "");
    if (!cleanPhone || cleanPhone.length < 6) { setError(t.err_invalid_phone); return; }
    const fullPhone = `${cc}${cleanPhone}`;
    setBusy(true);
    try {
      // Pull current strategy (single source of truth: same data displayed on screen)
      const strategy = (typeof window !== "undefined" && window.__aussieGuideStrategy) || null;
      const r = await axios.post(
        `${API}/guide-wa/download`,
        { name: name.trim(), email: email.trim().toLowerCase(), phone: fullPhone, source: source || "other", lang, consent: true, strategy },
        { responseType: "blob", withCredentials: true }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AussieCV_Guide_WA_${(name || "guide").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
      onClose(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(detail || t.err_generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[110] flex items-start md:items-center justify-center p-3 md:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => !busy && onClose(false)}
          data-testid="guide-lead-modal"
        >
          <motion.form
            onSubmit={submit}
            className="bg-white rounded-3xl w-full max-w-md shadow-2xl my-3 md:my-6 overflow-hidden"
            initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-[#D95D39] via-[#C24F2E] to-[#9B3B1E] text-white px-5 py-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/25 text-white text-[10px] font-bold mb-2">
                  <Compass className="w-3 h-3" /> WA GUIDE
                </div>
                <div className="font-heading font-extrabold text-xl md:text-2xl leading-tight">{t.title}</div>
                <div className="text-white/85 text-xs md:text-sm mt-1">{t.sub}</div>
              </div>
              <button type="button" onClick={() => !busy && onClose(false)} className="text-white/80 hover:text-white flex-shrink-0" data-testid="guide-lead-close" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-3.5">
              {/* Name */}
              <label className="block">
                <div className="text-xs font-semibold text-[#292524] mb-1 flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5 text-[#78716C]" /> {t.name}</div>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" data-testid="guide-lead-name" placeholder="Anas" maxLength={80} />
              </label>
              {/* Email */}
              <label className="block">
                <div className="text-xs font-semibold text-[#292524] mb-1 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#78716C]" /> {t.email} *</div>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="input" data-testid="guide-lead-email" placeholder="you@example.com" />
              </label>
              {/* Phone */}
              <div className="block">
                <div className="text-xs font-semibold text-[#292524] mb-1 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#78716C]" /> {t.phone_hint} *</div>
                <div className="flex gap-2">
                  <select value={cc} onChange={(e) => setCc(e.target.value)} className="input !w-auto !px-2 text-sm" data-testid="guide-lead-cc" aria-label="Country code">
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className="input flex-1" data-testid="guide-lead-phone" placeholder="612345678" inputMode="tel" />
                </div>
              </div>
              {/* Source */}
              <label className="block">
                <div className="text-xs font-semibold text-[#292524] mb-1">{t.source} *</div>
                <select value={source} onChange={(e) => setSource(e.target.value)} required className="input" data-testid="guide-lead-source">
                  <option value="">{t.src_placeholder}</option>
                  {Object.entries(t.sources).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                </select>
              </label>
              {/* Consent */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#D95D39] w-4 h-4 flex-shrink-0" data-testid="guide-lead-consent" />
                <span className="text-[11px] text-[#57534E] leading-snug">{t.consent}</span>
              </label>

              {error && <div className="text-xs text-[#D9423E] bg-[#FEF2F2] border border-[#F5CFCE] rounded-lg px-3 py-2" data-testid="guide-lead-error">{error}</div>}

              <button type="submit" disabled={busy} className="btn-primary w-full !py-3 mt-2" data-testid="guide-lead-submit">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> …</> : <><Download className="w-4 h-4" /> {t.download}</>}
              </button>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
